import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { targetPredictionHeaders } from '../../../configs/GridConfig'
import AgGridWrapper from '../../../components/common/AgGridWrapper'
import { apiConfig } from '../../../api/ApiConfig'
import { API_BASE_URL } from '../../../configs/Constants'
import { v4 as uuidv4 } from 'uuid'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Divider from '@mui/material/Divider'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import { TextField, Alert, Chip } from '@mui/material'
import { styled } from '@mui/material/styles'
import SpinnerButton from '../../../components/common/SpinnerButton'

const { getDtp } = apiConfig()

// Constants moved outside component
const SEARCH_STATES = {
  INIT: 'INIT',
  LOADING: 'LOADING',
  COMPLETE: 'COMPLETE',
  ERROR: 'ERROR',
}

// Styled components for professional appearance
const HeaderContainer = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
  color: '#FFFFFF',
  padding: theme.spacing(3, 4),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
}))

const ContentContainer = styled(Paper)(({ theme }) => ({
  background: '#FFFFFF',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  border: '1px solid #E5E7EB',
  marginBottom: theme.spacing(2),
}))

const SearchSection = styled(Card)(({ theme }) => ({
  background: '#F9FAFB',
  border: '1px solid #E5E7EB',
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
}))

const ResultsSection = styled(Box)(({ theme }) => ({
  background: '#FFFFFF',
  border: '1px solid #E5E7EB',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
}))

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiFilledInput-root': {
    backgroundColor: '#FFFFFF',
    border: '1px solid #D1D5DB',
    borderRadius: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#FFFFFF',
      borderColor: '#6B7280',
    },
    '&.Mui-focused': {
      backgroundColor: '#FFFFFF',
      borderColor: '#4B5563',
    }
  },
  '& .MuiInputLabel-root': {
    color: '#6B7280',
    '&.Mui-focused': {
      color: '#4B5563',
    }
  }
}))

const FeatureChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: '#FFFFFF',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  }
}))

export const TargetPrediction = () => {
  const [searchState, setSearchState] = useState(SEARCH_STATES.INIT)
  const [searchResult, setSearchResult] = useState([])
  const [smiles, setSmiles] = useState('')
  const [error, setError] = useState(null)
  const [isFirstClick, setIsFirstClick] = useState(true)
  const [targetpredfield, setTargetpredfield] = useState(
    localStorage.getItem('targetPredSmiles')
  )
  const [logs, setLogs] = useState([])
  const eventSourceRef = useRef(null)

  const startStreaming = useCallback((reqId) => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    const url = `${API_BASE_URL}/data/stream/dtp/logs/${reqId}`
    console.log('Starting stream:', url)

    const eventSource = new EventSource(url)
    eventSourceRef.current = eventSource

    eventSource.onmessage = (event) => {
      try {
        console.log('Stream message received:', event.data)
        const data = JSON.parse(event.data)
        if (data.error) {
          console.error('Stream error:', data.error)
          eventSource.close()
          return
        }
        const newLogs = Object.values(data)
        if (newLogs.length > 0) {
          console.log('Updating logs with:', newLogs)
          setLogs((prev) => [...prev, ...newLogs])
        }
      } catch (e) {
        console.error('Error parsing stream data:', e)
      }
    }

    eventSource.onerror = (err) => {
      console.error('EventSource failed:', err)
      eventSource.close()
    }
  }, [])

  const stopStreaming = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
    }
  }, [])

  // Validate SMILES input
  const validateSmiles = useCallback((smilesInput) => {
    const trimmedSmiles = smilesInput.trim()
    
    if (!trimmedSmiles) {
      setError('SMILES input is required')
      return false
    }

    if (trimmedSmiles.length < 3) {
      setError('SMILES must be at least 3 characters long')
      return false
    }

    // Basic SMILES validation - contains valid characters
    const validSmilesPattern = /^[A-Za-z0-9@+\-\[\]()=#$:.%\\/]+$/
    if (!validSmilesPattern.test(trimmedSmiles)) {
      setError('Invalid SMILES format')
      return false
    }

    return true
  }, [])

  // Main search function
  const search = useCallback(async () => {
    if (!validateSmiles(smiles)) {
      setSearchState(SEARCH_STATES.ERROR)
      return false
    }

    setSearchState(SEARCH_STATES.LOADING)
    setError(null)
    setLogs([])

    const reqId = uuidv4()
    startStreaming(reqId)

    try {
      const data = await getDtp(smiles.trim(), reqId)
      
      if (!data) {
        throw new Error('No data received from server')
      }

      const resultArray = Array.isArray(data) ? data : [data]
      setSearchResult(resultArray)
      localStorage.setItem('searchTargetResult', JSON.stringify(resultArray))
      setSearchState(SEARCH_STATES.COMPLETE)
      
      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Search failed. Please try again.'
      setError(errorMessage)
      setSearchState(SEARCH_STATES.ERROR)
      console.error('Target prediction search failed:', err)
      return false
    } finally {
      stopStreaming()
    }
  }, [smiles, validateSmiles, startStreaming, stopStreaming])

  // Handle initial data from localStorage
  useEffect(() => {
    if (targetpredfield) {
      setSmiles(targetpredfield)
      search()
    }
  }, [targetpredfield, search])

  // Load stored search results on component mount
  useEffect(() => {
    const storedSearchResult = localStorage.getItem('searchTargetResult')
    if (storedSearchResult) {
      try {
        const parsedResult = JSON.parse(storedSearchResult)
        setSearchResult(Array.isArray(parsedResult) ? parsedResult : [])
        setSearchState(SEARCH_STATES.COMPLETE)
      } catch (error) {
        console.error('Error parsing stored search result:', error)
        localStorage.removeItem('searchTargetResult')
      }
    }
  }, [])

  // Handle input changes
  const handleSmilesChange = useCallback((event) => {
    const value = event.target.value
    setSmiles(value)
    setError(null)
    
    // Clear search state if input becomes empty
    if (!value.trim() && searchState !== SEARCH_STATES.INIT) {
      setSearchState(SEARCH_STATES.INIT)
    }
  }, [searchState])

  // Clear input and results (original toggleReadOnly logic)
  const handleClear = useCallback(() => {
    if (isFirstClick) {
      setSmiles('')
      setIsFirstClick(false)
    } else {
      setSmiles('')
    }
    setSearchResult([])
    setLogs([])
    setSearchState(SEARCH_STATES.INIT)
    setError(null)
    localStorage.removeItem('targetPredSmiles')
    localStorage.removeItem('searchTargetResult')
  }, [isFirstClick])

  // Handle search button click
  const handleSearch = useCallback(async () => {
    await search()
  }, [search])

  // Memoized derived states
  const isLoading = useMemo(() => searchState === SEARCH_STATES.LOADING, [searchState])
  const hasError = useMemo(() => searchState === SEARCH_STATES.ERROR, [searchState])
  const isSearchDisabled = useMemo(() => !smiles.trim() || isLoading, [smiles, isLoading])
  const hasResults = useMemo(() => searchResult.length > 0, [searchResult])

  // Handle Enter key press in input
  const handleKeyPress = useCallback((event) => {
    if (event.key === 'Enter' && !isSearchDisabled) {
      handleSearch()
    }
  }, [handleSearch, isSearchDisabled])
  return (
    <Box>
      {/* Professional Header */}
      <HeaderContainer elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 600, 
                marginBottom: 1,
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Drug Target Prediction
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                opacity: 0.9,
                fontSize: '1.1rem',
                lineHeight: 1.5,
                marginBottom: 2
              }}
            >
              Predict potential biological targets for small molecules using AI-driven analysis
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <FeatureChip label="AI-Powered" size="small" />
              <FeatureChip label="Target Identification" size="small" />
              <FeatureChip label="Drug Discovery" size="small" />
            </Box>
          </Box>
          <Box sx={{ 
            display: { xs: 'none', md: 'block' },
            opacity: 0.3,
            fontSize: '4rem',
            fontWeight: 300
          }}>
            🎯
          </Box>
        </Box>
      </HeaderContainer>

      {/* Main Content */}
      <ContentContainer elevation={0}>
        {/* Search Section */}
        <SearchSection elevation={0}>
          <CardContent>
            <Typography 
              variant="h6" 
              sx={{ 
                marginBottom: 2, 
                color: '#374151',
                fontWeight: 600
              }}
            >
              Molecular Structure Input
            </Typography>
            <Divider sx={{ marginBottom: 3, backgroundColor: '#E5E7EB' }} />
            
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'flex-start',
                alignItems: 'flex-end',
                flexWrap: { xs: 'wrap', sm: 'nowrap' }
              }}
            >
              <Box sx={{ flex: 1, minWidth: '300px', maxWidth: '35rem' }}>
                <StyledTextField
                  variant="filled"
                  fullWidth
                  label="SMILES Notation"
                  placeholder="Enter SMILES notation (e.g., CCO, c1ccccc1)"
                  value={smiles}
                  onChange={handleSmilesChange}
                  onKeyPress={handleKeyPress}
                  error={Boolean(error)}
                  helperText={error || 'Enter a valid SMILES string to predict biological targets'}
                  disabled={isLoading}
                  autoComplete="off"
                />
              </Box>
              
              <SpinnerButton
                onClick={handleSearch}
                label="Predict Targets"
                loading={isLoading}
                disabled={isSearchDisabled}
                sx={{ 
                  height: '56px',
                  background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4B5563 0%, #374151 100%)',
                  },
                  fontWeight: 600,
                  textTransform: 'none',
                  minWidth: '140px'
                }}
              />
              
              <SpinnerButton
                onClick={handleClear}
                label="Clear"
                variant="outlined"
                disabled={isLoading}
                sx={{ 
                  height: '56px',
                  color: '#6B7280',
                  borderColor: '#D1D5DB',
                  minWidth: '80px',
                  '&:hover': {
                    borderColor: '#6B7280',
                    backgroundColor: '#F9FAFB'
                  }
                }}
              />
            </Box>
          </CardContent>
        </SearchSection>

        {/* Logs Section */}
        {(isLoading || logs.length > 0) && (
          <Box sx={{ marginBottom: 3 }}>
             <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: '#1e1e1e',
                color: '#e0e0e0',
                borderRadius: 2,
                fontFamily: 'monospace',
                maxHeight: '200px',
                overflowY: 'auto',
                fontSize: '0.875rem',
                border: '1px solid #333'
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#90caf9',
                  mb: 1,
                  borderBottom: '1px solid #333',
                  pb: 0.5
                }}
              >
                Processing Logs
              </Typography>
              {logs.map((log, index) => (
                <Box key={index} sx={{ py: 0.25 }}>
                  <span style={{ color: '#66bb6a', marginRight: '8px' }}>➜</span>
                  {log}
                </Box>
              ))}
              {isLoading && (
                <Box sx={{ py: 0.25, fontStyle: 'italic', opacity: 0.7 }}>
                  <span style={{ display: 'inline-block', animation: 'pulse 1.5s infinite' }}>...</span>
                </Box>
              )}
            </Paper>
          </Box>
        )}

        {/* Results Section */}
        <Box>
          <Typography 
            variant="h6" 
            sx={{ 
              marginBottom: 2, 
              color: '#374151',
              fontWeight: 600
            }}
          >
            Prediction Results
          </Typography>
          <Divider sx={{ marginBottom: 2, backgroundColor: '#E5E7EB' }} />

          {/* Results summary */}
          {hasResults && searchState === SEARCH_STATES.COMPLETE && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 2,
                backgroundColor: '#ECFDF5',
                borderColor: '#A7F3D0',
                color: '#065F46'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography>
                  Found <strong>{searchResult.length}</strong> target prediction{searchResult.length !== 1 ? 's' : ''} for the provided SMILES
                </Typography>
              </Box>
            </Alert>
          )}

          {/* No results message */}
          {searchState === SEARCH_STATES.COMPLETE && !hasResults && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: 2,
                backgroundColor: '#EFF6FF',
                borderColor: '#BFDBFE',
                color: '#1E40AF'
              }}
            >
              No target predictions found for the provided SMILES notation. Try a different molecular structure.
            </Alert>
          )}

          <ResultsSection>
            <AgGridWrapper
              data={searchResult}
              headers={targetPredictionHeaders}
              loading={isLoading}
              error={hasError}
              api={search}
            />
          </ResultsSection>
        </Box>
      </ContentContainer>
    </Box>
  )
}
